/**
 * eventTree — every event beneath an event, as the tree the Event Detail page's navigator draws.
 *
 * Built from the two reads useEventHierarchy already makes, because neither is enough alone:
 *
 *   nodes      the campaign-hierarchy tree beneath the event. Has the shape — including external
 *              (SFDC-only) campaigns between events — but no names, and it lists every node,
 *              including events this caller may not see.
 *   subEvents  ListSubEvents: every event beneath it, at any depth, the caller may see, with names,
 *              in the service's order (depth-first, siblings by start date). It leaves out a hidden
 *              event and everything beneath it.
 *
 * So the tree is `nodes`, cut to what `subEvents` admits:
 *
 *   - An event node is kept only if ListSubEvents returned its event; otherwise it goes with its whole
 *     subtree, which is what the service did too. A BD so gets no row for a DRAFT or another BU's
 *     event, nor for anything under one.
 *   - A campaign node stays as a grouping row — an event under an external campaign under this event
 *     is still one of its nested events (PM request: show them all) — but only while something visible
 *     remains beneath it. A branch of bare campaign ids has no event to open; the Event Hierarchy field
 *     in Basic Information is where those show.
 *   - Siblings are ordered by the first ListSubEvents position in their subtree. ListSubEvents is
 *     depth-first, so that reproduces its order for events and slots each campaign where its first
 *     event falls.
 *
 * `parentEventId` on a row is the nearest event above it, the root event when there is none — the same
 * rule as SubEvent.parentEventId and as the Parent Event field: an external campaign is not a level.
 */

import type { Event, SubEvent } from '../client/EventCalendarClient';
import type { HierarchyNode } from '../components/CampaignHierarchy/campaignHierarchy';

export interface EventTreeEventRow {
  kind: 'event';
  key: string;
  event: Event & { eventId: string };
  /** The nearest event above this one: an event in the tree, or the root event. */
  parentEventId: string;
  children: EventTreeRow[];
}

export interface EventTreeCampaignRow {
  kind: 'campaign';
  key: string;
  sfdcCampaignId: string;
  children: EventTreeRow[];
}

export type EventTreeRow = EventTreeEventRow | EventTreeCampaignRow;

interface Built {
  row: EventTreeRow;
  /** The smallest ListSubEvents position in the row's subtree, for ordering siblings. */
  order: number;
}

/**
 * The visible tree beneath `rootEventId` (see the header). Empty until both reads are in, and when
 * either failed: with no names there is nothing to put on a row, and with no shape no tree to draw.
 */
export function visibleEventTree(
  rootEventId: string | undefined,
  nodes: readonly HierarchyNode[] | null | undefined,
  subEvents: readonly SubEvent[] | undefined,
): EventTreeRow[] {
  if (!rootEventId || !nodes || !subEvents) return [];
  const visible = new Map<string, { event: Event & { eventId: string }; order: number }>();
  subEvents.forEach((s, order) => {
    const id = s.event?.eventId;
    if (id && !visible.has(id)) visible.set(id, { event: { ...s.event!, eventId: id }, order });
  });

  function build(list: readonly HierarchyNode[], parentEventId: string): Built[] {
    const out: Built[] = [];
    for (const node of list) {
      if (node.eventId !== undefined) {
        const seen = node.eventId ? visible.get(node.eventId) : undefined;
        if (!seen) continue; // hidden (or a blank event row) — and so is everything beneath it
        const children = build(node.children, seen.event.eventId);
        out.push({
          row: {
            kind: 'event',
            key: node.key,
            event: seen.event,
            parentEventId,
            children: children.map((c) => c.row),
          },
          order: Math.min(seen.order, ...children.map((c) => c.order)),
        });
      } else {
        const children = build(node.children, parentEventId);
        if (!children.length) continue;
        out.push({
          row: {
            kind: 'campaign',
            key: node.key,
            sfdcCampaignId: node.sfdcCampaignId,
            children: children.map((c) => c.row),
          },
          order: Math.min(...children.map((c) => c.order)),
        });
      }
    }
    return out.sort((a, b) => a.order - b.order);
  }

  return build(nodes, rootEventId).map((b) => b.row);
}

/** Every event row of the tree, depth-first. */
export function eventRows(tree: readonly EventTreeRow[]): EventTreeEventRow[] {
  const out: EventTreeEventRow[] = [];
  const walk = (rows: readonly EventTreeRow[]) => {
    for (const r of rows) {
      if (r.kind === 'event') out.push(r);
      walk(r.children);
    }
  };
  walk(tree);
  return out;
}

/**
 * The event rows from the top of the tree down to `eventId`'s, inclusive — the path the navigator's
 * detail pane shows above a nested event. Empty when the event is not in the tree.
 */
export function eventPath(tree: readonly EventTreeRow[], eventId: string): EventTreeEventRow[] {
  for (const r of tree) {
    if (r.kind === 'event' && r.event.eventId === eventId) return [r];
    const below = eventPath(r.children, eventId);
    if (below.length) return r.kind === 'event' ? [r, ...below] : below;
  }
  return [];
}

/**
 * The tree with only the rows whose event name contains `query` (case-insensitive), plus their
 * ancestors so each match keeps its place. A blank query returns the tree as it is.
 */
export function filterEventTree(tree: readonly EventTreeRow[], query: string): EventTreeRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...tree];
  const out: EventTreeRow[] = [];
  for (const r of tree) {
    const hit = r.kind === 'event' && (r.event.eventName ?? '').toLowerCase().includes(q);
    // A matching event keeps its whole subtree: its children are part of what was found.
    const children = hit ? r.children : filterEventTree(r.children, query);
    if (hit || children.length) out.push({ ...r, children });
  }
  return out;
}
